import { ErrorMessage } from '@hookform/error-message';

import { ReactNode, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  Button,
  DateRangePicker,
  DateValuePicker,
  FormControl,
  FormItem,
  Modal,
  OptionList,
  Select,
  SelectTrigger,
} from '@ingrid/luterkort-react';
import { ShippingLogo } from '@ingrid/react-shipping-logo';

import { useTranslation } from '~src/i18n/use-translation';
import { useShippingMethodListSuspenseQuery } from '~src/main/queries';
import { now, time, toLocalCalendarDate, toLocalCalendarDateTime, today } from '~src/utils/internationalized-date';

import { ShipmentUpsert } from '../types';

type FormValues = Pick<ShipmentUpsert, 'shipping_method' | 'delivery_time' | 'shipping_date'>;

export interface CarrierProductSelectProps {
  mode: 'add' | 'update';
  value?: FormValues;
  onSubmit: (values: FormValues) => void;
  trigger: ReactNode;
}

export function CarrierProductSelect(props: CarrierProductSelectProps) {
  const { mode, value, onSubmit: onSubmitProp, trigger } = props;
  const [modalOpen, setModalOpen] = useState(false);

  const { control, formState, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: value,
  });

  const { data: shippingMethods } = useShippingMethodListSuspenseQuery({ customBookingMethods: true });
  const { t, i18n } = useTranslation();

  function handleOpenChange(isOpen: boolean) {
    setModalOpen(isOpen);

    if (!isOpen) {
      reset(value);
    }
  }

  function onSubmit(payload: FormValues) {
    onSubmitProp(payload);
    setModalOpen(false);
  }

  return (
    <FormControl.Root>
      <Modal.Root open={modalOpen} onOpenChange={handleOpenChange}>
        <Modal.Trigger>{trigger}</Modal.Trigger>

        <Modal.Content>
          <FormControl.Form onSubmit={handleSubmit(onSubmit)} style={{ display: 'none' }} />

          <Modal.Header title={t('common.carrierProduct')} />

          <Modal.Body css={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <Controller
              rules={{ required: t('errors.fieldIsRequired') }}
              name="shipping_method"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormItem.Root>
                  <FormItem.Label>{t('common.carrierProduct')}</FormItem.Label>
                  <FormItem.Control>
                    <Select.Root
                      value={value?.id ? [value.id] : undefined}
                      onValueChange={v => onChange(shippingMethods.find(c => c.id === v[0]))}
                    >
                      <Select.Trigger data-invalid={!!formState.errors.shipping_method}>
                        <SelectTrigger.Button>
                          <SelectTrigger.Placeholder>{t('common.selectCarrierProduct')}</SelectTrigger.Placeholder>

                          {shippingMethods.map(method => (
                            <SelectTrigger.Value key={method.id} value={method.id!}>
                              <SelectTrigger.Logo>
                                <ShippingLogo method={method.id} width={30} />
                              </SelectTrigger.Logo>
                              <span>{method.name}</span>
                            </SelectTrigger.Value>
                          ))}
                          <SelectTrigger.Icon />
                        </SelectTrigger.Button>
                      </Select.Trigger>

                      <Select.Content css={{ width: '608px' }}>
                        <OptionList.Viewport>
                          {shippingMethods.map(carrier => (
                            <OptionList.Item key={carrier.id} value={carrier.id!}>
                              <OptionList.ItemLogo>
                                <ShippingLogo method={carrier.id} width={30} />
                              </OptionList.ItemLogo>
                              <OptionList.ItemText>{carrier.name}</OptionList.ItemText>
                              <OptionList.ItemIndicator />
                            </OptionList.Item>
                          ))}
                        </OptionList.Viewport>
                      </Select.Content>
                    </Select.Root>
                  </FormItem.Control>
                  <ErrorMessage as={FormItem.ErrorText} errors={formState.errors} name="shipping_method" />
                </FormItem.Root>
              )}
            />

            <Controller
              rules={{ required: t('errors.fieldIsRequired') }}
              name="shipping_date"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormItem.Root>
                  <FormItem.Label>{t('common.shippingDate')}</FormItem.Label>
                  <FormItem.Control invalid={!!formState.errors.shipping_date?.message}>
                    <DateValuePicker
                      minValue={today('UTC')}
                      locale={i18n.resolvedLanguage}
                      isInvalid={!!formState.errors.shipping_date}
                      aria-required="true"
                      placeholder={t('common.selectDate')}
                      today={today('UTC')}
                      onChange={v => onChange(v!.toString())}
                      value={value ? toLocalCalendarDate(value) : undefined}
                    />
                  </FormItem.Control>
                  {!formState.errors.shipping_date && (
                    <FormItem.HintText>{t('shipments.carrierProduct.shippingTimeHint')}</FormItem.HintText>
                  )}
                  <ErrorMessage as={FormItem.ErrorText} errors={formState.errors} name="shipping_date" />
                </FormItem.Root>
              )}
            />

            <Controller
              name="delivery_time"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormItem.Root data-testid="delivery-time">
                  <FormItem.Label>{t('common.deliveryTime')}</FormItem.Label>
                  <FormItem.Control>
                    <DateRangePicker
                      onChange={v =>
                        onChange(v?.start && v?.end ? { start: v.start.toString(), end: v.end.toString() } : undefined)
                      }
                      value={
                        value?.start && value?.end
                          ? { start: toLocalCalendarDateTime(value.start), end: toLocalCalendarDateTime(value.end) }
                          : undefined
                      }
                      defaultTime={{ start: time(0, 0), end: time(0, 0) }}
                      placeholder={t('common.selectDate')}
                      locale={i18n.resolvedLanguage}
                      minValue={now('UTC')}
                      granularity="minute"
                      today={today('UTC')}
                    />
                  </FormItem.Control>
                  <FormItem.HintText>{t('shipments.carrierProduct.deliveryTimeHint')}</FormItem.HintText>
                </FormItem.Root>
              )}
            />
          </Modal.Body>

          <Modal.Footer>
            <Modal.CloseButton>
              <Button variant="secondary">{t('common.cancel')}</Button>
            </Modal.CloseButton>

            <FormControl.SubmitButton asChild>
              <Button>{t(mode === 'add' ? 'common.addCarrier' : 'common.updateCarrier')}</Button>
            </FormControl.SubmitButton>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </FormControl.Root>
  );
}
